<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const props = defineProps({
  pipeline: {
    type: Object,
    required: true,
  },
  stages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:mappings', 'update:valid']);

const { t } = useI18n();
const store = useStore();

// Track initialization state to prevent race conditions
const isInitialized = ref(false);
const lastPipelineId = ref(null);

// Get labels from store
const labels = computed(() => store.getters['labels/getLabels']);

// Initialize mappings from pipeline settings
const labelStageMappings = ref([]);
const stageLabelMappings = ref([]);

// Actions for label->stage mappings
const labelStageActions = [
  { value: 'move', label: t('PIPELINE.LABEL_MAPPINGS.ACTIONS.MOVE') },
  { value: 'mark_won', label: t('PIPELINE.LABEL_MAPPINGS.ACTIONS.MARK_WON') },
  {
    value: 'mark_lost',
    label: t('PIPELINE.LABEL_MAPPINGS.ACTIONS.MARK_LOST'),
  },
];

// Actions for stage->label mappings
const stageLabelActions = [
  { value: 'add', label: t('PIPELINE.LABEL_MAPPINGS.ACTIONS.ADD_LABEL') },
  {
    value: 'remove',
    label: t('PIPELINE.LABEL_MAPPINGS.ACTIONS.REMOVE_LABEL'),
  },
];

// All stages including won/lost
const allStages = computed(() => props.stages);

// Validation: check if any mapping has empty required fields
const isLabelStageMappingValid = mapping => {
  if (!mapping.label_title) return false;
  if (mapping.action === 'move' && !mapping.target_stage) return false;
  return true;
};

const isStageLabelMappingValid = mapping => {
  if (!mapping.stage) return false;
  if (!mapping.label_title) return false;
  return true;
};

const hasInvalidMappings = computed(() => {
  const invalidLS = labelStageMappings.value.some(
    m => !isLabelStageMappingValid(m)
  );
  const invalidSL = stageLabelMappings.value.some(
    m => !isStageLabelMappingValid(m)
  );
  return invalidLS || invalidSL;
});

// Check if a stage exists in current stages list
const stageExists = stageId => {
  if (!stageId) return true; // Empty is valid (not yet selected)
  return props.stages.some(s => s.id === stageId);
};

// Check if a label exists in current labels list
const labelExists = labelTitle => {
  if (!labelTitle) return true; // Empty is valid (not yet selected)
  return labels.value.some(
    l => l.title.toLowerCase() === labelTitle.toLowerCase()
  );
};

// Validate mappings before emitting - returns cleaned data
const validateMappings = mappingsData => {
  const cleanedLabelStage = mappingsData.label_stage_mappings.map(m => ({
    ...m,
    // Ensure required fields have defaults
    id:
      m.id ||
      `mapping_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
    enabled: m.enabled !== undefined ? m.enabled : true,
    action: m.action || 'move',
  }));

  const cleanedStageLabel = mappingsData.stage_label_mappings.map(m => ({
    ...m,
    id:
      m.id ||
      `mapping_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
    enabled: m.enabled !== undefined ? m.enabled : true,
    action: m.action || 'add',
  }));

  return {
    label_stage_mappings: cleanedLabelStage,
    stage_label_mappings: cleanedStageLabel,
  };
};

// Emit update to parent with validation
const emitUpdate = () => {
  if (!isInitialized.value) return;

  const validatedData = validateMappings({
    label_stage_mappings: labelStageMappings.value,
    stage_label_mappings: stageLabelMappings.value,
  });

  emit('update:mappings', validatedData);
  emit('update:valid', !hasInvalidMappings.value);
};

const initializeMappings = () => {
  const settings = props.pipeline?.settings || {};
  labelStageMappings.value = [...(settings.label_stage_mappings || [])];
  stageLabelMappings.value = [...(settings.stage_label_mappings || [])];
};

// Fetch labels on mount
onMounted(async () => {
  await store.dispatch('labels/get');
  initializeMappings();
  lastPipelineId.value = props.pipeline?.id;
  await nextTick();
  isInitialized.value = true;
  emit('update:valid', !hasInvalidMappings.value);
});

// Watch for pipeline changes - only reinitialize when pipeline ID changes
// This prevents race conditions when user is editing mappings
watch(
  () => props.pipeline?.id,
  newId => {
    if (newId && newId !== lastPipelineId.value) {
      lastPipelineId.value = newId;
      isInitialized.value = false;
      initializeMappings();
      nextTick(() => {
        isInitialized.value = true;
      });
    }
  }
);

// Generate unique ID
const generateId = () => {
  return `mapping_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

// Add new label->stage mapping
const addLabelStageMapping = () => {
  labelStageMappings.value.push({
    id: generateId(),
    label_title: '',
    target_stage: '',
    action: 'move',
    lost_reason: '',
    enabled: true,
  });
  emitUpdate();
};

// Add new stage->label mapping
const addStageLabelMapping = () => {
  stageLabelMappings.value.push({
    id: generateId(),
    stage: '',
    label_title: '',
    action: 'add',
    enabled: true,
  });
  emitUpdate();
};

// Remove label->stage mapping
const removeLabelStageMapping = index => {
  labelStageMappings.value.splice(index, 1);
  emitUpdate();
};

// Remove stage->label mapping
const removeStageLabelMapping = index => {
  stageLabelMappings.value.splice(index, 1);
  emitUpdate();
};

// Toggle mapping enabled state
const toggleLabelStageEnabled = index => {
  labelStageMappings.value[index].enabled =
    !labelStageMappings.value[index].enabled;
  emitUpdate();
};

const toggleStageLabelEnabled = index => {
  stageLabelMappings.value[index].enabled =
    !stageLabelMappings.value[index].enabled;
  emitUpdate();
};

// Handle mapping field change
const onMappingChange = () => {
  emitUpdate();
};
</script>

<template>
  <div class="space-y-6">
    <!-- Label -> Stage Mappings -->
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('PIPELINE.LABEL_MAPPINGS.LABEL_TO_STAGE.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ t('PIPELINE.LABEL_MAPPINGS.LABEL_TO_STAGE.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Mapping rules list -->
      <div class="space-y-2">
        <div
          v-for="(mapping, index) in labelStageMappings"
          :key="mapping.id"
          class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg"
        >
          <div class="flex items-start gap-2">
            <!-- Enable toggle -->
            <label
              class="relative inline-flex items-center cursor-pointer mt-6"
            >
              <input
                type="checkbox"
                :checked="mapping.enabled"
                class="sr-only peer"
                @change="toggleLabelStageEnabled(index)"
              />
              <div
                class="w-9 h-5 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-n-brand"
              />
            </label>

            <!-- Fields grid -->
            <div class="flex-1 grid grid-cols-3 gap-2 items-end">
              <!-- Label select -->
              <div>
                <label class="block text-xs text-n-slate-11 mb-1">
                  {{ t('PIPELINE.LABEL_MAPPINGS.LABEL') }}
                </label>
                <select
                  v-model="mapping.label_title"
                  class="w-full px-2 py-1.5 text-sm border rounded focus:border-n-brand"
                  :class="[
                    !labelExists(mapping.label_title) || !mapping.label_title
                      ? 'border-n-ruby-9 bg-n-ruby-3'
                      : 'border-n-weak bg-n-solid-2 text-n-slate-12',
                  ]"
                  @change="onMappingChange"
                >
                  <option value="">
                    {{ t('PIPELINE.LABEL_MAPPINGS.SELECT_LABEL') }}
                  </option>
                  <option
                    v-if="
                      mapping.label_title && !labelExists(mapping.label_title)
                    "
                    :value="mapping.label_title"
                    class="text-n-ruby-11"
                  >
                    {{
                      t('PIPELINE.LABEL_MAPPINGS.DELETED_ITEM', {
                        name: mapping.label_title,
                      })
                    }}
                  </option>
                  <option
                    v-for="label in labels"
                    :key="label.id"
                    :value="label.title"
                  >
                    {{ label.title }}
                  </option>
                </select>
              </div>

              <!-- Action select -->
              <div>
                <label class="block text-xs text-n-slate-11 mb-1">
                  {{ t('PIPELINE.LABEL_MAPPINGS.ACTION') }}
                </label>
                <select
                  v-model="mapping.action"
                  class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-n-solid-2 text-n-slate-12 focus:border-n-brand"
                  @change="onMappingChange"
                >
                  <option
                    v-for="action in labelStageActions"
                    :key="action.value"
                    :value="action.value"
                  >
                    {{ action.label }}
                  </option>
                </select>
              </div>

              <!-- Stage select (only for move action) -->
              <div v-if="mapping.action === 'move'">
                <label class="block text-xs text-n-slate-11 mb-1">
                  {{ t('PIPELINE.LABEL_MAPPINGS.STAGE') }}
                </label>
                <select
                  v-model="mapping.target_stage"
                  class="w-full px-2 py-1.5 text-sm border rounded focus:border-n-brand"
                  :class="[
                    !stageExists(mapping.target_stage) || !mapping.target_stage
                      ? 'border-n-ruby-9 bg-n-ruby-3'
                      : 'border-n-weak bg-n-solid-2 text-n-slate-12',
                  ]"
                  @change="onMappingChange"
                >
                  <option value="">
                    {{ t('PIPELINE.LABEL_MAPPINGS.SELECT_STAGE') }}
                  </option>
                  <option
                    v-if="
                      mapping.target_stage && !stageExists(mapping.target_stage)
                    "
                    :value="mapping.target_stage"
                    class="text-n-ruby-11"
                  >
                    {{
                      t('PIPELINE.LABEL_MAPPINGS.DELETED_ITEM', {
                        name: mapping.target_stage,
                      })
                    }}
                  </option>
                  <option
                    v-for="stage in allStages"
                    :key="stage.id"
                    :value="stage.id"
                  >
                    {{ stage.name }}
                  </option>
                </select>
              </div>

              <!-- Lost reason (only for mark_lost action) -->
              <div v-if="mapping.action === 'mark_lost'">
                <label class="block text-xs text-n-slate-11 mb-1">
                  {{ t('PIPELINE.LABEL_MAPPINGS.LOST_REASON') }}
                </label>
                <input
                  v-model="mapping.lost_reason"
                  type="text"
                  class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-n-solid-2 text-n-slate-12 focus:border-n-brand"
                  :placeholder="
                    t('PIPELINE.LABEL_MAPPINGS.LOST_REASON_PLACEHOLDER')
                  "
                  @input="onMappingChange"
                />
              </div>
            </div>

            <!-- Remove button -->
            <button
              type="button"
              class="p-1.5 text-n-slate-10 hover:text-n-ruby-11 transition-colors mt-5"
              @click="removeLabelStageMapping(index)"
            >
              <fluent-icon icon="delete" size="16" />
            </button>
          </div>
        </div>

        <!-- Add button -->
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-2 text-sm text-n-brand hover:bg-n-alpha-1 rounded-lg transition-colors"
          @click="addLabelStageMapping"
        >
          <fluent-icon icon="add" size="16" />
          {{ t('PIPELINE.LABEL_MAPPINGS.ADD_LABEL_STAGE_RULE') }}
        </button>
      </div>
    </div>

    <!-- Divider -->
    <hr class="border-n-weak" />

    <!-- Stage -> Label Mappings -->
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('PIPELINE.LABEL_MAPPINGS.STAGE_TO_LABEL.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ t('PIPELINE.LABEL_MAPPINGS.STAGE_TO_LABEL.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Mapping rules list -->
      <div class="space-y-2">
        <div
          v-for="(mapping, index) in stageLabelMappings"
          :key="mapping.id"
          class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg"
        >
          <div class="flex items-start gap-2">
            <!-- Enable toggle -->
            <label
              class="relative inline-flex items-center cursor-pointer mt-6"
            >
              <input
                type="checkbox"
                :checked="mapping.enabled"
                class="sr-only peer"
                @change="toggleStageLabelEnabled(index)"
              />
              <div
                class="w-9 h-5 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-n-brand"
              />
            </label>

            <!-- Fields grid -->
            <div class="flex-1 grid grid-cols-3 gap-2 items-end">
              <!-- Stage select -->
              <div>
                <label class="block text-xs text-n-slate-11 mb-1">
                  {{ t('PIPELINE.LABEL_MAPPINGS.STAGE') }}
                </label>
                <select
                  v-model="mapping.stage"
                  class="w-full px-2 py-1.5 text-sm border rounded focus:border-n-brand"
                  :class="[
                    !stageExists(mapping.stage) || !mapping.stage
                      ? 'border-n-ruby-9 bg-n-ruby-3'
                      : 'border-n-weak bg-n-solid-2 text-n-slate-12',
                  ]"
                  @change="onMappingChange"
                >
                  <option value="">
                    {{ t('PIPELINE.LABEL_MAPPINGS.SELECT_STAGE') }}
                  </option>
                  <option
                    v-if="mapping.stage && !stageExists(mapping.stage)"
                    :value="mapping.stage"
                    class="text-n-ruby-11"
                  >
                    {{
                      t('PIPELINE.LABEL_MAPPINGS.DELETED_ITEM', {
                        name: mapping.stage,
                      })
                    }}
                  </option>
                  <option
                    v-for="stage in allStages"
                    :key="stage.id"
                    :value="stage.id"
                  >
                    {{ stage.name }}
                  </option>
                </select>
              </div>

              <!-- Action select -->
              <div>
                <label class="block text-xs text-n-slate-11 mb-1">
                  {{ t('PIPELINE.LABEL_MAPPINGS.ACTION') }}
                </label>
                <select
                  v-model="mapping.action"
                  class="w-full px-2 py-1.5 text-sm border border-n-weak rounded bg-n-solid-2 text-n-slate-12 focus:border-n-brand"
                  @change="onMappingChange"
                >
                  <option
                    v-for="action in stageLabelActions"
                    :key="action.value"
                    :value="action.value"
                  >
                    {{ action.label }}
                  </option>
                </select>
              </div>

              <!-- Label select -->
              <div>
                <label class="block text-xs text-n-slate-11 mb-1">
                  {{ t('PIPELINE.LABEL_MAPPINGS.LABEL') }}
                </label>
                <select
                  v-model="mapping.label_title"
                  class="w-full px-2 py-1.5 text-sm border rounded focus:border-n-brand"
                  :class="[
                    !labelExists(mapping.label_title) || !mapping.label_title
                      ? 'border-n-ruby-9 bg-n-ruby-3'
                      : 'border-n-weak bg-n-solid-2 text-n-slate-12',
                  ]"
                  @change="onMappingChange"
                >
                  <option value="">
                    {{ t('PIPELINE.LABEL_MAPPINGS.SELECT_LABEL') }}
                  </option>
                  <option
                    v-if="
                      mapping.label_title && !labelExists(mapping.label_title)
                    "
                    :value="mapping.label_title"
                    class="text-n-ruby-11"
                  >
                    {{
                      t('PIPELINE.LABEL_MAPPINGS.DELETED_ITEM', {
                        name: mapping.label_title,
                      })
                    }}
                  </option>
                  <option
                    v-for="label in labels"
                    :key="label.id"
                    :value="label.title"
                  >
                    {{ label.title }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Remove button -->
            <button
              type="button"
              class="p-1.5 text-n-slate-10 hover:text-n-ruby-11 transition-colors mt-5"
              @click="removeStageLabelMapping(index)"
            >
              <fluent-icon icon="delete" size="16" />
            </button>
          </div>
        </div>

        <!-- Add button -->
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-2 text-sm text-n-brand hover:bg-n-alpha-1 rounded-lg transition-colors"
          @click="addStageLabelMapping"
        >
          <fluent-icon icon="add" size="16" />
          {{ t('PIPELINE.LABEL_MAPPINGS.ADD_STAGE_LABEL_RULE') }}
        </button>
      </div>
    </div>

    <!-- Info box -->
    <div class="p-3 bg-n-blue-3 border border-n-blue-9 rounded-lg">
      <div class="flex items-start gap-2">
        <fluent-icon
          icon="info"
          size="16"
          class="text-n-blue-11 mt-0.5 flex-shrink-0"
        />
        <div class="text-xs text-n-blue-11 space-y-1">
          <p>{{ t('PIPELINE.LABEL_MAPPINGS.INFO.LINE_1') }}</p>
          <p>{{ t('PIPELINE.LABEL_MAPPINGS.INFO.LINE_2') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
